import React, { useEffect, useState } from 'react';
import {
  hqExpiryService,
  ExpirySummary,
  ExpiryStoreDetail,
} from '../services/hqExpiryService';

const HqExpiryPage: React.FC = () => {
  const [data, setData] = useState<ExpirySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailCache, setDetailCache] = useState<Record<string, ExpiryStoreDetail>>({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await hqExpiryService.summary());
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? '만료 현황은 본부 대표만 볼 수 있습니다.'
          : e?.response?.data?.detail || '만료 현황을 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleExpand = async (orgId: string) => {
    if (expandedId === orgId) return setExpandedId(null);
    setExpandedId(orgId);
    if (!detailCache[orgId]) {
      try {
        const d = await hqExpiryService.storeMembers(orgId, 14);
        setDetailCache((prev) => ({ ...prev, [orgId]: d }));
      } catch {
        /* 무시 */
      }
    }
  };

  if (loading) return <div className="p-4 text-slate-500">불러오는 중…</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!data) return null;

  const { totals, stores } = data;
  const alimtalkOff = stores.filter((s) => !s.alimtalk_expiry_on);

  return (
    <div className="p-4 sm:p-6 max-w-5xl">
      <h1 className="text-xl sm:text-2xl font-bold mb-1">⏳ 이용권 만료 임박</h1>
      <p className="text-xs text-slate-500 mb-4">
        기준일 {data.today} · {data.note}
      </p>

      {/* 요약 밴드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">이미 만료</div>
          <div className="text-lg sm:text-xl font-bold text-red-600">
            {totals.already_expired}명
          </div>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">7일 이내</div>
          <div className="text-lg sm:text-xl font-bold text-orange-600">
            {totals.within_7}명
          </div>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">14일 이내</div>
          <div className="text-lg sm:text-xl font-bold text-amber-600">
            {totals.within_14}명
          </div>
        </div>
        <div className="bg-white rounded-xl border p-3">
          <div className="text-xs text-slate-500">이용 중</div>
          <div className="text-lg sm:text-xl font-bold text-slate-800">
            {totals.active_valid}명
          </div>
        </div>
      </div>

      {/* 알림톡 꺼진 매장 경고 */}
      {alimtalkOff.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 sm:p-4">
          <div className="font-semibold text-amber-900 text-sm mb-1">
            ⚠️ 만료 예정 알림톡이 꺼진 매장 {alimtalkOff.length}곳
          </div>
          <div className="text-xs text-amber-800 leading-relaxed">
            {alimtalkOff.map((s) => s.name).join(' · ')}
            <br />
            해당 매장은 만료 7일·3일·1일 전 안내가 학생에게 자동 발송되지 않습니다.
            <br />
            켜는 곳: 매장 화면 → 연동 설정 → 알림 탭 → 카카오 알림톡 상세 설정 →
            이용권·결제 → 이용권 만료
          </div>
        </div>
      )}

      {/* 점포별 표 */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">점포</th>
              <th className="text-right px-3 py-2 font-medium">이미 만료</th>
              <th className="text-right px-3 py-2 font-medium">7일 이내</th>
              <th className="text-right px-3 py-2 font-medium">14일 이내</th>
              <th className="text-right px-3 py-2 font-medium">이용 중</th>
              <th className="text-center px-3 py-2 font-medium">만료 알림톡</th>
              <th className="text-center px-3 py-2 font-medium">만료 시 게이트</th>
            </tr>
          </thead>
          <tbody>
            {stores.map((s) => {
              const detail = detailCache[s.organization_id];
              const open = expandedId === s.organization_id;
              return (
                <React.Fragment key={s.organization_id}>
                  <tr
                    className="border-t hover:bg-slate-50 cursor-pointer"
                    onClick={() => toggleExpand(s.organization_id)}
                  >
                    <td className="px-3 py-2 font-medium">
                      {open ? '▾ ' : '▸ '}
                      {s.name}
                    </td>
                    <td className="px-3 py-2 text-right text-red-600">
                      {s.already_expired || '-'}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-orange-600">
                      {s.within_7 || '-'}
                    </td>
                    <td className="px-3 py-2 text-right text-amber-700">
                      {s.within_14 || '-'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{s.active_valid}</td>
                    <td className="px-3 py-2 text-center">
                      {s.alimtalk_expiry_on ? (
                        <span className="text-emerald-600">켜짐</span>
                      ) : (
                        <span className="text-red-600 font-semibold">꺼짐</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-slate-600">
                      {s.gate_block_expired ? '차단' : '통과'}
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-slate-50">
                      <td colSpan={7} className="px-3 py-3">
                        {!detail ? (
                          <div className="text-xs text-slate-500">불러오는 중…</div>
                        ) : detail.members.length === 0 ? (
                          <div className="text-xs text-slate-500">
                            14일 이내 만료 예정 학생이 없습니다.
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {detail.members.map((m, i) => (
                              <span
                                key={i}
                                className={`text-xs px-2 py-1 rounded-lg border ${
                                  m.days_left < 0
                                    ? 'bg-red-50 border-red-200 text-red-700'
                                    : m.days_left <= 7
                                    ? 'bg-orange-50 border-orange-200 text-orange-700'
                                    : 'bg-white border-slate-200 text-slate-600'
                                }`}
                              >
                                {m.name} · {m.expiry_date.slice(5)}
                                {m.days_left < 0
                                  ? ` (${-m.days_left}일 지남)`
                                  : ` (D-${m.days_left})`}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3 leading-relaxed">
        · 만료일 당일까지는 이용 가능합니다. 연장을 이미 마친 학생은 집계에서 빠집니다.
        <br />
        · 연장 여부는 매장에서 결정합니다. 본부는 현황만 확인합니다.
      </p>
    </div>
  );
};

export default HqExpiryPage;
